import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "@tanstack/react-router";
import { AlertTriangle, CalendarRange, CheckCircle2 } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { ROOMS } from "@/lib/rooms";
import { FLEET } from "@/lib/fleet";
import { occupiedNights } from "@/lib/lodge-inventory";

const DAY = 86_400_000;

/**
 * Weeks run Friday to Friday: guests leave Friday morning and the next party
 * arrives Friday midday, so the window is the seven nights from the most recent
 * Friday through Thursday night, with changeover on the following Friday.
 */
export function weekWindow(today = new Date()) {
  const base = Date.parse(`${today.toISOString().slice(0, 10)}T00:00:00Z`);
  const dow = new Date(base).getUTCDay(); // 0 Sun … 5 Fri
  const backToFriday = (dow - 5 + 7) % 7;
  const start = base - backToFriday * DAY;
  const nights: string[] = [];
  for (let i = 0; i < 7; i += 1) nights.push(new Date(start + i * DAY).toISOString().slice(0, 10));
  return nights;
}

type Booking = {
  id: string;
  name: string;
  arrival_date: string | null;
  departure_date: string | null;
  party_size: number | null;
  notes: string | null;
};

const shortDay = (iso: string) =>
  new Date(`${iso}T00:00:00Z`).toLocaleDateString("en-US", { timeZone: "UTC", weekday: "short", day: "numeric" });

export function WeekAssignmentCard() {
  const nights = useMemo(() => weekWindow(), []);
  const [bookings, setBookings] = useState<Booking[]>([]);
  const [roomRows, setRoomRows] = useState<Array<{ booking_id: string; room_id: string; night: string }>>([]);
  const [boatRows, setBoatRows] = useState<Array<{ booking_id: string; boat_name: string; night: string }>>([]);

  const first = nights[0];
  const last = nights[nights.length - 1];
  /** Departure day: the Friday after the last night of the week. */
  const changeover = new Date(Date.parse(`${last}T00:00:00Z`) + DAY).toISOString().slice(0, 10);

  const load = useCallback(async () => {
    const [b, r, bo] = await Promise.all([
      supabase
        .from("booking_requests")
        .select("id,name,arrival_date,departure_date,party_size,notes")
        .eq("status", "approved")
        .lte("arrival_date", last)
        .limit(500),
      supabase.from("room_assignments").select("booking_id,room_id,night").gte("night", first).lte("night", last),
      supabase.from("boat_assignments").select("booking_id,boat_name,night").gte("night", first).lte("night", last),
    ]);
    setBookings(((b.data ?? []) as Booking[]).filter((x) => occupiedNights(x as never).some((n) => nights.includes(n))));
    setRoomRows((r.data ?? []) as never);
    setBoatRows((bo.data ?? []) as never);
  }, [first, last, nights]);

  useEffect(() => {
    void load();
  }, [load]);

  const byNight = useMemo(
    () =>
      nights.map((night) => {
        const roomsTaken = roomRows.filter((x) => String(x.night).slice(0, 10) === night).length;
        const boatsTaken = boatRows.filter((x) => String(x.night).slice(0, 10) === night).length;
        return {
          night,
          roomsFree: Math.max(0, ROOMS.length - roomsTaken),
          boatsFree: Math.max(0, FLEET.length - boatsTaken),
        };
      }),
    [nights, roomRows, boatRows],
  );

  /** Approved stays this week that Heather hasn't put in a room / on a boat yet. */
  const needsRoom = bookings.filter((b) => !roomRows.some((r) => r.booking_id === b.id));
  const needsBoat = bookings.filter((b) => {
    const wantsBoat = /boats:\s*[1-9]/i.test(b.notes ?? "");
    return wantsBoat && !boatRows.some((r) => r.booking_id === b.id);
  });

  const minRooms = Math.min(...byNight.map((n) => n.roomsFree));
  const minBoats = Math.min(...byNight.map((n) => n.boatsFree));

  return (
    <section aria-label="This week's rooms and boats" className="mt-8 rounded-2xl border border-border bg-card/60 p-6">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <div className="flex items-center gap-2 text-[var(--color-ember)]">
            <CalendarRange size={16} aria-hidden />
            <span className="text-xs font-semibold uppercase tracking-[0.25em]">This week (Fri–Fri)</span>
          </div>
          <h2 className="mt-2 font-display text-xl">Unassigned rooms &amp; boats</h2>
          <p className="mt-1 text-sm text-muted-foreground">
            {shortDay(first)} – {shortDay(changeover)} · guests leave Friday morning and the next
            party arrives Friday midday. Longer stays are counted on every night they cover.
          </p>
        </div>
        <Link
          to="/admin/bookings"
          className="rounded-md border border-border px-3 py-2 text-sm hover:bg-muted"
        >
          Assign now
        </Link>
      </div>

      <div className="mt-5 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <Stat label="Rooms unassigned (tightest night)" value={`${minRooms} of ${ROOMS.length}`} />
        <Stat label="Boats unassigned (tightest night)" value={`${minBoats} of ${FLEET.length}`} />
        <Stat
          label="Stays with no room yet"
          value={String(needsRoom.length)}
          warn={needsRoom.length > 0}
        />
        <Stat
          label="Boat rentals with no boat yet"
          value={String(needsBoat.length)}
          warn={needsBoat.length > 0}
        />
      </div>

      <div className="mt-5 overflow-x-auto">
        <table className="w-full min-w-[520px] text-sm">
          <thead>
            <tr className="text-left text-xs uppercase tracking-wider text-muted-foreground">
              <th className="py-2">Night</th>
              <th className="py-2">Rooms open</th>
              <th className="py-2">Boats open</th>
            </tr>
          </thead>
          <tbody>
            {byNight.map((n) => (
              <tr key={n.night} className="border-t border-border/60">
                <td className="py-2">{shortDay(n.night)}</td>
                <td className="py-2">
                  {n.roomsFree} / {ROOMS.length}
                </td>
                <td className="py-2">
                  {n.boatsFree} / {FLEET.length}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {(needsRoom.length > 0 || needsBoat.length > 0) && (
        <ul className="mt-4 space-y-1 text-sm">
          {needsRoom.map((b) => (
            <li key={`r-${b.id}`} className="flex items-center gap-2 text-amber-500">
              <AlertTriangle size={14} aria-hidden /> {b.name} — no room assigned
            </li>
          ))}
          {needsBoat.map((b) => (
            <li key={`b-${b.id}`} className="flex items-center gap-2 text-amber-500">
              <AlertTriangle size={14} aria-hidden /> {b.name} — boat rental with no boat assigned
            </li>
          ))}
        </ul>
      )}
      {bookings.length > 0 && needsRoom.length === 0 && needsBoat.length === 0 && (
        <p className="mt-4 flex items-center gap-2 text-sm text-emerald-500">
          <CheckCircle2 size={14} aria-hidden /> Every stay this week has a room and a boat assigned.
        </p>
      )}
      {bookings.length === 0 && <p className="mt-4 text-sm text-muted-foreground">No approved stays this week.</p>}
    </section>
  );
}

function Stat({ label, value, warn }: { label: string; value: string; warn?: boolean }) {
  return (
    <div className="rounded-xl border border-border bg-background/60 p-4">
      <p className="text-xs uppercase tracking-wider text-muted-foreground">{label}</p>
      <p className={`mt-1 font-display text-2xl ${warn ? "text-amber-500" : ""}`}>{value}</p>
    </div>
  );
}
